/**
 * "Where did my balance come from?"
 *
 * A disputed leave balance is one of the most common HR support tickets, and the usual
 * answer — someone opens the database — does not scale and is not auditable. So the
 * ledger is a first-class screen: every accrual, grant, deduction, carry-over and
 * expiry, in order, with a running balance.
 *
 * The `reconciles` flag deserves the prominence it gets. The API replays the ledger and
 * compares the result to the cached balance. When they disagree, the CACHED figure is the
 * one the user has been shown everywhere else and it is wrong. Hiding that behind a
 * console warning would mean the first person to notice is an employee who was refused
 * leave they were entitled to.
 */
import { availableFromBuckets, useLeaveLedger } from '@frontend/api/hooks/leave';
import { formatDate, humanise } from '@frontend/lib/format';
import { cn } from '@frontend/lib/cn';
import { Drawer } from '@frontend/components/ui/Modal';
import { Banner } from '@frontend/components/ui/Page';
import { EmptyState, ErrorState, LoadingRows } from '@frontend/components/ui/States';

export function LedgerDrawer({
  employeeId,
  leaveType,
  year,
  onClose,
}: {
  employeeId: string | null;
  leaveType: string | null;
  year: number;
  onClose: () => void;
}): JSX.Element | null {
  const query = useLeaveLedger(employeeId, leaveType, year);
  if (leaveType === null) return null;

  return (
    <Drawer
      open
      onClose={onClose}
      title={`${humanise(leaveType)} — how this balance was calculated`}
      width="w-[520px]"
      data-testid="ledger-drawer"
    >
      {query.isLoading ? (
        <LoadingRows rows={6} columns={3} />
      ) : query.isError ? (
        <ErrorState
          error={query.error}
          onRetry={() => {
            void query.refetch();
          }}
        />
      ) : query.data === undefined ? (
        <EmptyState
          title="No ledger for this leave type"
          description="A ledger starts the first time this leave type is accrued, granted or used."
        />
      ) : (
        <div className="space-y-4 p-4">
          {/*
            `replayedFromLedger` is a BUCKET SET, not a balance view — it carries no
            `availableDays`. This banner used to read that field straight off it, so the
            one moment the screen most needs to work — the balance is WRONG and the
            employee must be warned — rendered "undefined days available".
          */}
          {!query.data.reconciles && (
            <Banner tone="error" title="This balance does not match its history">
              Replaying the entries below gives {availableFromBuckets(query.data.replayedFromLedger)} days
              available, but the stored balance says {query.data.balance.availableDays}. The entries are the
              source of truth. Report this to HR before booking leave against it — do not rely on the stored
              figure.
            </Banner>
          )}

          <section>
            <h3 className="mb-2 text-row font-semibold text-content">Where it stands</h3>
            <dl className="grid grid-cols-2 gap-3">
              {Object.entries(query.data.buckets).map(([key, value]) => (
                <div key={key}>
                  <dt className="text-body uppercase tracking-[0.04em] text-content-tertiary">{humanise(key)}</dt>
                  <dd className="text-section font-semibold tabular text-content">{value}</dd>
                </div>
              ))}
            </dl>
          </section>

          <section>
            <h3 className="mb-2 text-row font-semibold text-content">Every movement</h3>
            {query.data.entries.length === 0 ? (
              <p className="text-row text-content-secondary">
                No movements recorded for {year}. The balance shown comes entirely from the opening position.
              </p>
            ) : (
              <ol className="space-y-0" data-testid="ledger-entries">
                {/*
                  FIELD NAMES ARE THE SERVER'S. An entry is
                  `{ id, effectiveDate, kind, days, code, text, balanceAfterDays }`. Reading
                  `entryType` / `deltaDays` / `balanceAfter` / `description` produced a row
                  with a date, an em dash and nothing else — no amount, no running balance,
                  no explanation — and no error, because `humanise(undefined)` returns a
                  dash rather than throwing.
                */}
                {query.data.entries.map((entry) => {
                  const delta = Number(entry.days);
                  return (
                    <li
                      key={entry.id}
                      className="flex items-start gap-3 border-b border-line-subtle py-2 last:border-0"
                      data-testid={`ledger-entry-${entry.id}`}
                    >
                      <span className="w-[86px] shrink-0 text-body tabular text-content-tertiary">
                        {formatDate(entry.effectiveDate)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-row text-content">{humanise(entry.kind)}</span>
                        {entry.text !== '' && (
                          <span className="block text-body text-content-tertiary">{entry.text}</span>
                        )}
                      </span>
                      <span
                        className={cn(
                          'w-[64px] shrink-0 text-right text-row font-medium tabular',
                          delta < 0 ? 'text-error' : 'text-success',
                        )}
                      >
                        {delta > 0 ? '+' : ''}
                        {entry.days}
                      </span>
                      <span className="w-[56px] shrink-0 text-right text-body tabular text-content-tertiary">
                        {entry.balanceAfterDays}
                      </span>
                    </li>
                  );
                })}
              </ol>
            )}
          </section>

          <p className="text-body text-content-secondary">
            Accruals, carry-over caps and expiry dates are policy settings an administrator controls. If a
            figure looks wrong, the entry that produced it names its source.
          </p>
        </div>
      )}
    </Drawer>
  );
}
